import { useState, useEffect } from 'react';
import { artistApi } from '../services/artistApi';

export const useArtistProfileImage = (artistId?: number) => {
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Store current URL for cleanup
    let currentImageUrl: string | null = null;
    let isCancelled = false; // Flag to prevent state updates after cleanup

    if (!artistId) {
      setProfileImageUrl(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    const loadProfileImage = async () => {
      if (isCancelled) return;
      
      try {
        setIsLoading(true);
        setError(null);
        
        // Add a timeout to prevent hanging requests
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Request timeout')), 10000); // 10 second timeout
        });
        
        const imagePromise = artistApi.getProfileImage(artistId);
        const imageBlob = await Promise.race([imagePromise, timeoutPromise]) as Blob;
        
        if (isCancelled) return; // Don't update state if component unmounted
        
        currentImageUrl = URL.createObjectURL(imageBlob);
        setProfileImageUrl(currentImageUrl);
      } catch (err) {
        if (isCancelled) return;
        
        // If the image doesn't exist or fails to load, that's okay - we'll show the default icon
        setError(err instanceof Error ? err.message : 'Failed to load profile image');
        setProfileImageUrl(null);
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    loadProfileImage();

    // Cleanup function to revoke the object URL when component unmounts or artistId changes
    return () => {
      isCancelled = true;
      if (currentImageUrl) {
        URL.revokeObjectURL(currentImageUrl);
      }
    };
  }, [artistId]);

  return { profileImageUrl, isLoading, error };
};